'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Eye, EyeOff, AlertCircle, CheckCircle, Lock, ArrowLeft, Info } from 'lucide-react';
import Link from 'next/link';

type SetupState = 'checking' | 'available' | 'locked' | 'success' | 'error';

interface FormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function AdminSetupContent() {
  const router = useRouter();
  const supabase = createClient();

  const [setupState, setSetupState] = useState<SetupState>('checking');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Check if admin already exists on mount
  useEffect(() => {
    checkAdminExists();
  }, []);

  const checkAdminExists = async () => {
    setSetupState('checking');
    try {
      // Primary: try the RPC function
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_admin_count');

      if (!rpcError && rpcData !== null && rpcData !== undefined) {
        setSetupState(rpcData > 0 ? 'locked' : 'available');
        return;
      }

      // Fallback: direct table query if RPC fails (table may not exist yet)
      const { data: tableData, error: tableError } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('role', 'admin')
        .eq('is_active', true);

      if (!tableError) {
        const { count } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('role', 'admin')
          .eq('is_active', true);
        setSetupState((count ?? 0) > 0 ? 'locked' : 'available');
        return;
      }

      // If both fail (table doesn't exist yet), allow setup
      setSetupState('available');
    } catch {
      // Network/config error — still allow setup attempt
      setSetupState('available');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setErrorMsg('');
  };

  const validate = (): string | null => {
    if (!formData.fullName.trim()) return 'Full name is required.';
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email))
      return 'A valid email address is required.';
    if (formData.password.length < 8) return 'Password must be at least 8 characters.';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setIsLoading(true);
    setErrorMsg('');

    try {
      // Double-check no admin exists (race condition guard)
      try {
        const { data: adminCount } = await supabase.rpc('get_admin_count');
        if (adminCount > 0) {
          setSetupState('locked');
          return;
        }
      } catch {
        // RPC not available yet — proceed with signup
      }

      // Create the admin account via Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName.trim(),
            role: 'admin',
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Account creation failed. Please try again.');

      // Upsert user_profiles with admin role
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert(
          {
            id: authData.user.id,
            email: formData.email.trim().toLowerCase(),
            full_name: formData.fullName.trim(),
            role: 'admin',
            is_active: true,
          },
          { onConflict: 'id' }
        );

      if (profileError) {
        console.warn('Profile upsert warning (non-fatal):', profileError.message);
      }

      setSetupState('success');
    } catch (err: any) {
      const msg = err?.message || 'An unexpected error occurred.';
      if (
        msg.toLowerCase().includes('already registered') ||
        msg.toLowerCase().includes('already exists') ||
        msg.toLowerCase().includes('user already registered')
      ) {
        setErrorMsg(
          'This email is already registered. Use the Sign In page to log in with your existing account.'
        );
      } else if (msg.toLowerCase().includes('invalid email')) {
        setErrorMsg('Please enter a valid email address.');
      } else if (msg.toLowerCase().includes('password')) {
        setErrorMsg('Password must be at least 8 characters long.');
      } else {
        setErrorMsg(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Checking state ──────────────────────────────────────────────────────────
  if (setupState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-600/30 border-t-teal-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Verifying system status…</p>
        </div>
      </div>
    );
  }

  // ── Locked state (admin already exists) ────────────────────────────────────
  if (setupState === 'locked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={24} className="text-amber-600" />
          </div>
          <h2 className="font-semibold text-gray-900 text-xl mb-2">Setup Already Complete</h2>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            A system administrator account already exists. This setup page is no longer accessible for security reasons.
          </p>
          <Link
            href="/sign-up-login-screen"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <ArrowLeft size={15} />
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  // ── Success state ───────────────────────────────────────────────────────────
  if (setupState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={24} className="text-teal-600" />
          </div>
          <h2 className="font-semibold text-gray-900 text-xl mb-2">Administrator Account Created!</h2>
          <p className="text-gray-500 text-sm mb-2 leading-relaxed">
            Your system administrator account has been created successfully.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 mb-6 text-left">
            <div className="flex items-start gap-2">
              <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-blue-700 text-xs leading-relaxed">
                If email confirmation is enabled in your Supabase project, check your inbox and confirm your email before signing in.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              href="/admin-onboarding"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Continue to Onboarding Setup
              <ArrowLeft size={15} className="rotate-180" />
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-semibold rounded-xl transition-colors"
            >
              <ArrowLeft size={15} />
              Proceed to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Available state (main form) ─────────────────────────────────────────────
  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-[42%] bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 flex-col justify-center p-14 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-teal-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-teal-900/40 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-10">
            <AppLogo size={44} />
            <div>
              <span className="font-semibold text-white text-2xl block leading-tight">STARGA LMS</span>
              <span className="text-teal-200 text-xs font-medium tracking-wide">Vocational Learning Platform</span>
            </div>
          </div>
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6 border border-white/20">
            <Shield size={28} className="text-teal-200" />
          </div>
          <h1 className="font-semibold text-white text-3xl leading-tight mb-4">
            System<br />
            <span className="text-teal-200">Administrator</span><br />
            Setup
          </h1>
          <p className="text-teal-100 text-sm leading-relaxed max-w-xs">
            This is a one-time setup page. Once the first administrator account is created, this page will be permanently locked for security.
          </p>
          <div className="mt-8 space-y-3">
            {[
              'One-time access — locks after first admin is created',
              'Full administrator privileges granted immediately',
              'Continue to onboarding to configure system settings',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-5 h-5 bg-teal-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle size={11} className="text-teal-200" />
                </div>
                <span className="text-teal-100 text-xs leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={36} />
          <span className="font-semibold text-teal-700 text-xl">STARGA LMS</span>
        </div>

        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-7">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                <Shield size={16} className="text-teal-700" />
              </div>
              <span className="text-xs font-semibold text-teal-700 uppercase tracking-widest">Initial Setup</span>
            </div>
            <h2 className="font-semibold text-gray-900 text-2xl mb-1">Create Administrator Account</h2>
            <p className="text-gray-500 text-sm">Set up the first system administrator for STARGA LMS.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="e.g. Sarah Johnson"
                autoComplete="name"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-colors"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@stargalms.edu"
                autoComplete="email"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-colors"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error message */}
            {errorMsg && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                <AlertCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-600 text-xs leading-relaxed">{errorMsg}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-teal-700 hover:bg-teal-800 active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:scale-100 mt-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating Administrator Account…
                </>
              ) : (
                <>
                  <Shield size={15} />
                  Create Administrator Account
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            Already have an account?{' '}
            <Link href="/sign-up-login-screen" className="text-teal-600 hover:underline font-medium">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
